import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

/**
 * DestructiveConfirmDialog - Enhanced confirmation dialog for destructive actions
 * 
 * Features:
 * - Clear warning icon
 * - Explicit description of what will be deleted
 * - Optional reason input for audit trail
 * - Red confirm button that stands out
 * - Primary cancel escape
 */
export function DestructiveConfirmDialog({
  open,
  onOpenChange,
  title = "Delete Item",
  itemName,
  itemType = "item",
  description,
  onConfirm,
  loading = false,
  confirmText = "Delete",
  requireReason = false,
  reasonMinLength = 10,
}) {
  const [reason, setReason] = useState("");
  const defaultDescription = `Are you sure you want to delete ${itemType} "${itemName}"? This action is permanent and cannot be undone.`;
  
  const isReasonValid = !requireReason || reason.trim().length >= reasonMinLength;

  const handleConfirm = () => {
    if (requireReason && !isReasonValid) return;
    onConfirm(reason.trim() || undefined);
  };

  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setReason(""); // Reset reason when dialog closes
    }
    onOpenChange(newOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="bg-card border-white/10">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/10 rounded-full">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <AlertDialogTitle className="text-lg">{title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2 text-sm">
            {description || defaultDescription}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        {requireReason && (
          <div className="space-y-2 py-2">
            <Label htmlFor="deletion-reason" className="text-sm font-medium">
              Reason for deletion <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="deletion-reason"
              placeholder={`Enter reason (minimum ${reasonMinLength} characters)...`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[80px] bg-surface border-white/10"
              data-testid="deletion-reason-input"
            />
            {reason.length > 0 && reason.length < reasonMinLength && (
              <p className="text-xs text-red-400">
                {reasonMinLength - reason.length} more characters required
              </p>
            )}
          </div>
        )}
        
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel 
            className="bg-transparent border-white/10 hover:bg-white/5"
            disabled={loading}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={loading || !isReasonValid}
            className="bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50"
            data-testid="confirm-delete-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Deleting...
              </span>
            ) : (
              confirmText
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DestructiveConfirmDialog;
